import React, { useState, useEffect, useRef } from 'react';
import { 
  Video, 
  Plus, 
  Trash2, 
  Edit, 
  Calendar, 
  Play, 
  Pause, 
  Search, 
  Filter, 
  AlertCircle, 
  User, 
  Clock, 
  Save,
  Upload,
  Camera,
  Mic,
  Square,
  Download,
  Image as ImageIcon,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { SearchInput } from '../common/SearchInput';

interface VideoMessage {
  id: string;
  title: string;
  description?: string;
  date: string;
  url: string;
  thumbnail?: string;
  recipients?: string[];
  duration: string;
}

export function Videos() {
  const { user } = useAuth();
  const [videos, setVideos] = useState<VideoMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoMessage | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const [recordingStream, setRecordingStream] = useState<MediaStream | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // File upload states
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  const [selectedThumbnailFile, setSelectedThumbnailFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    url: '',
    thumbnail: '',
    recipients: [''],
    duration: ''
  });

  // Common recipients
  const commonRecipients = [
    'Spouse', 'Son', 'Daughter', 'Grandchild', 'Friend', 'Sibling', 'Parent', 'All Family Members'
  ];

  useEffect(() => {
    const loadVideos = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Load videos from database
        const { data, error } = await supabase
          .from('legacy_videos')
          .select('*')
          .eq('user_id', user.id)
          .order('date', { ascending: false });
          
        if (error) throw error;
        
        setVideos(data || []);
      } catch (err) {
        console.error('Error loading videos:', err);
        setError(err instanceof Error ? err.message : 'Failed to load videos');
      } finally {
        setLoading(false);
      }
    };
    
    loadVideos();
  }, [user]);

  // Initialize camera when modal opens
  useEffect(() => {
    if (showAddModal && !cameraActive) {
      initializeCamera();
    }
    
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (recordingStream) {
        recordingStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [showAddModal, cameraActive]);

  const initializeCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: true 
      });
      
      setRecordingStream(stream);
      setCameraActive(true);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Error initializing camera:', err);
      setError('Failed to access camera. Please check permissions.');
    }
  };

  const startRecording = async () => {
    if (!recordingStream) return;
    
    try {
      // Try different MIME types in order of preference
      const mimeTypes = [
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp9,opus',
        'video/webm',
        'video/mp4;codecs=h264,aac',
        'video/mp4',
        'video/ogg;codecs=theora,vorbis',
        'video/ogg'
      ];
      
      let mimeType = null;
      for (const type of mimeTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }
      
      if (!mimeType) {
        throw new Error('No supported video format found for recording');
      }
      
      console.log('Using MIME type for recording:', mimeType);
      
      const recorder = new MediaRecorder(recordingStream, {
        mimeType: mimeType
      });
      const chunks: Blob[] = [];
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        setRecordedChunks([blob]);
        
        // Create a URL for the recorded video
        const videoUrl = URL.createObjectURL(blob);
        setFormData(prev => ({ ...prev, url: videoUrl }));
        
        // Calculate duration
        const duration = Math.floor(recordingTime / 60);
        setFormData(prev => ({ ...prev, duration: `${duration} minutes` }));
        
        console.log('Recording completed:', {
          blobSize: blob.size,
          blobType: blob.type,
          duration: recordingTime,
          mimeType: mimeType
        });
      };
      
      recorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        setError('Recording failed due to a technical error.');
      };
      
      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      console.error('Error starting recording:', err);
      setError(`Failed to start recording: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  const handleVideoFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedVideoFile(file);
      const videoUrl = URL.createObjectURL(file);
      setFormData(prev => ({ ...prev, url: videoUrl }));
      
      // Try to get duration from video file
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        const duration = Math.floor(video.duration / 60);
        setFormData(prev => ({ ...prev, duration: `${duration} minutes` }));
      };
      video.src = videoUrl;
    }
  };

  const handleThumbnailFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedThumbnailFile(file);
      const thumbnailUrl = URL.createObjectURL(file);
      setFormData(prev => ({ ...prev, thumbnail: thumbnailUrl }));
    }
  };

  const uploadVideoToStorage = async (file: File): Promise<string> => {
    try {
      // Validate file size (100MB limit)
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        throw new Error(`File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds the 100MB limit`);
      }

      // Validate file type
      const allowedTypes = ['video/webm', 'video/mp4', 'video/avi', 'video/mov', 'video/quicktime'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error(`File type ${file.type} is not supported. Allowed types: ${allowedTypes.join(', ')}`);
      }

      const fileName = `${user?.id}/videos/${Date.now()}_${file.name}`;
      
      console.log('Uploading video:', {
        fileName,
        fileType: file.type,
        fileSize: file.size,
        fileSizeMB: (file.size / 1024 / 1024).toFixed(2),
        bucket: 'legacy-media'
      });
      
      const { data, error } = await supabase.storage
        .from('legacy-media')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (error) {
        console.error('Storage upload error:', error);
        throw new Error(`Upload failed: ${error.message}`);
      }
      
      console.log('Video uploaded successfully:', data);
      
      const { data: { publicUrl } } = supabase.storage
        .from('legacy-media')
        .getPublicUrl(fileName);
      
      console.log('Public URL generated:', publicUrl);
      
      return publicUrl;
    } catch (err) {
      console.error('Error in uploadVideoToStorage:', err);
      throw err;
    }
  };

  const uploadThumbnailToStorage = async (file: File): Promise<string> => {
    try {
      // Validate file size (5MB limit for thumbnails)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error(`Thumbnail file size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds the 5MB limit`);
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error(`Thumbnail file type ${file.type} is not supported. Allowed types: ${allowedTypes.join(', ')}`);
      }

      const fileName = `${user?.id}/thumbnails/${Date.now()}_${file.name}`;
      
      console.log('Uploading thumbnail:', {
        fileName,
        fileType: file.type,
        fileSize: file.size,
        fileSizeMB: (file.size / 1024 / 1024).toFixed(2),
        bucket: 'legacy-media'
      });
      
      const { data, error } = await supabase.storage
        .from('legacy-media')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (error) {
        console.error('Thumbnail upload error:', error);
        throw new Error(`Thumbnail upload failed: ${error.message}`);
      }
      
      console.log('Thumbnail uploaded successfully:', data);
      
      const { data: { publicUrl } } = supabase.storage
        .from('legacy-media')
        .getPublicUrl(fileName);
      
      console.log('Thumbnail public URL generated:', publicUrl);
      
      return publicUrl;
    } catch (err) {
      console.error('Error in uploadThumbnailToStorage:', err);
      throw err;
    }
  };

  const handleAddVideo = () => {
    setFormData({
      title: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      url: '',
      thumbnail: '',
      recipients: [''],
      duration: ''
    });
    setIsEditing(false);
    setSelectedVideoFile(null);
    setSelectedThumbnailFile(null);
    setRecordedChunks([]);
    setRecordingTime(0);
    setError(null);
    setSuccessMessage(null);
    setShowAddModal(true);
  };

  const handleEditVideo = (video: VideoMessage) => {
    setSelectedVideo(video);
    setFormData({
      title: video.title,
      description: video.description || '',
      date: video.date,
      url: video.url,
      thumbnail: video.thumbnail || '',
      recipients: video.recipients || [''],
      duration: video.duration
    });
    setIsEditing(true);
    setError(null);
    setSuccessMessage(null);
    setShowAddModal(true);
  };

  const handleViewVideo = (video: VideoMessage) => {
    setSelectedVideo(video);
    setShowViewModal(true);
  };

  const handleDeleteVideo = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this video message? This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('legacy_videos')
          .delete()
          .eq('id', id);
          
        if (error) throw error;
        
        setVideos(videos.filter(video => video.id !== id));
      } catch (err) {
        console.error('Error deleting video:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete video');
      }
    }
  };

  const handleAddRecipient = () => {
    setFormData({
      ...formData,
      recipients: [...formData.recipients, '']
    });
  };

  const handleRemoveRecipient = (index: number) => {
    const newRecipients = [...formData.recipients];
    newRecipients.splice(index, 1);
    setFormData({
      ...formData,
      recipients: newRecipients
    });
  };

  const handleRecipientChange = (index: number, value: string) => {
    const newRecipients = [...formData.recipients];
    newRecipients[index] = value;
    setFormData({
      ...formData,
      recipients: newRecipients
    });
  };

  const handleSaveVideo = async () => {
    // Validate form
    if (!formData.title || !formData.url || !formData.date || !formData.duration) {
      setError('Please fill in all required fields');
      return;
    }
    
    // Filter out empty recipients
    const filteredRecipients = formData.recipients.filter(recipient => recipient.trim() !== '');
    
    try {
      setUploadingVideo(true);
      setUploadProgress(0);
      
      let finalVideoUrl = formData.url;
      let finalThumbnailUrl = formData.thumbnail;
      
      // Upload recorded video if it exists
      if (recordedChunks.length > 0) {
        const videoBlob = recordedChunks[0];
        const fileExtension = videoBlob.type.includes('mp4') ? 'mp4' : 'webm';
        const videoFile = new File([videoBlob], `recorded_video_${Date.now()}.${fileExtension}`, {
          type: videoBlob.type
        });
        finalVideoUrl = await uploadVideoToStorage(videoFile);
        setUploadProgress(50);
      } else if (selectedVideoFile) {
        // Upload selected video file
        finalVideoUrl = await uploadVideoToStorage(selectedVideoFile);
        setUploadProgress(50);
      }
      
      // Upload thumbnail if selected
      if (selectedThumbnailFile) {
        finalThumbnailUrl = await uploadThumbnailToStorage(selectedThumbnailFile);
        setUploadProgress(100);
      }
      
      if (isEditing && selectedVideo) {
        // Update existing video
        const { error } = await supabase
          .from('legacy_videos')
          .update({
            title: formData.title,
            description: formData.description || null,
            date: formData.date,
            url: finalVideoUrl,
            thumbnail: finalThumbnailUrl || null,
            recipients: filteredRecipients.length > 0 ? filteredRecipients : null,
            duration: formData.duration
          })
          .eq('id', selectedVideo.id);
          
        if (error) throw error;
        
        // Update local state
        setVideos(videos.map(video => 
          video.id === selectedVideo.id 
            ? {
                ...video,
                title: formData.title,
                description: formData.description || null,
                date: formData.date,
                url: finalVideoUrl,
                thumbnail: finalThumbnailUrl || null,
                recipients: filteredRecipients.length > 0 ? filteredRecipients : null,
                duration: formData.duration
              }
            : video
        ));
      } else {
        // Create new video
        const { data, error } = await supabase
          .from('legacy_videos')
          .insert([{
            user_id: user?.id,
            title: formData.title,
            description: formData.description || null,
            date: formData.date,
            url: finalVideoUrl,
            thumbnail: finalThumbnailUrl || null,
            recipients: filteredRecipients.length > 0 ? filteredRecipients : null,
            duration: formData.duration
          }])
          .select()
          .single();
          
        if (error) throw error;
        
        setVideos([data, ...videos]);
      }
      
      setSuccessMessage('Video saved successfully!');
      setShowAddModal(false);
      setFormData({
        title: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        url: '',
        thumbnail: '',
        recipients: [''],
        duration: ''
      });
      setSelectedVideoFile(null);
      setSelectedThumbnailFile(null);
      setRecordedChunks([]);
      setRecordingTime(0);
      
    } catch (err) {
      console.error('Error saving video:', err);
      let errorMessage = 'Failed to save video';
      
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'object' && err !== null && 'message' in err) {
        errorMessage = String(err.message);
      }
      
      setError(errorMessage);
      setSuccessMessage(null);
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const filteredVideos = videos.filter(video => {
    const matchesSearch = 
      video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.recipients?.some(recipient => 
        recipient.toLowerCase().includes(searchQuery.toLowerCase())
      );
    
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Video Messages</h1>
          <p className="text-gray-600 mt-1">Record and share video messages with your loved ones</p>
        </div>
        <Button
          variant="primary"
          onClick={handleAddVideo}
          icon={<Plus size={20} />}
        >
          Add Video
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle size={20} className="text-red-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-red-700 font-medium">Upload Error</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-xs text-red-500 hover:text-red-700 mt-2 underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
              <div className="w-2 h-2 bg-white rounded-full"></div>
            </div>
            <div className="flex-1">
              <p className="text-green-700 font-medium">Success</p>
              <p className="text-green-600 text-sm mt-1">{successMessage}</p>
              <button
                onClick={() => setSuccessMessage(null)}
                className="text-xs text-green-500 hover:text-green-700 mt-2 underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex gap-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search videos..."
          className="flex-1"
        />
        <Button
          variant="outline"
          icon={<Filter size={20} />}
        >
          Filters
        </Button>
      </div>

      {/* Video Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredVideos.map(video => (
          <Card key={video.id} className="overflow-hidden">
            <div className="relative">
              {video.thumbnail ? (
                <img 
                  src={video.thumbnail} 
                  alt={video.title} 
                  className="w-full h-48 object-cover"
                />
              ) : (
                <div className="w-full h-48 bg-purple-100 flex items-center justify-center">
                  <Video className="text-purple-600" size={32} />
                </div>
              )}
              <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                {video.duration}
              </div>
              <div className="absolute top-2 left-2 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewVideo(video)}
                  className="bg-white/90 hover:bg-white"
                >
                  <Play size={16} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEditVideo(video)}
                  className="bg-white/90 hover:bg-white"
                >
                  <Edit size={16} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteVideo(video.id)}
                  className="bg-white/90 hover:bg-white text-red-600 hover:text-red-700"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
            <div className="p-4">
              <h3 className="font-semibold text-gray-900 mb-2">{video.title}</h3>
              {video.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{video.description}</p>
              )}
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                <Calendar size={14} />
                <span>{formatDate(video.date)}</span>
              </div>
              {video.recipients && video.recipients.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {video.recipients.map((recipient, index) => (
                    <span key={index} className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full">
                      {recipient}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {filteredVideos.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Video className="text-purple-600" size={24} />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Videos Yet</h3>
          <p className="text-gray-500 mb-4">
            {searchQuery
              ? 'No videos match your search criteria'
              : 'Start recording video messages for your loved ones'}
          </p>
          {!searchQuery && (
            <Button
              variant="primary"
              onClick={handleAddVideo}
              icon={<Plus size={20} />}
            >
              Record Your First Video
            </Button>
          )}
        </div>
      )}

      {/* Compact Add/Edit Video Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={isEditing ? 'Edit Video Message' : 'Add Video Message'}
        maxWidth="2xl"
      >
        <div className="space-y-4">
          {/* Camera Preview - Compact */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-900">Camera Preview</h3>
              {isRecording && (
                <div className="bg-red-600 text-white px-2 py-1 rounded text-xs font-medium">
                  REC {formatTime(recordingTime)}
                </div>
              )}
            </div>
            
            <div className="relative">
              <video
                ref={videoRef}
                className="w-full h-48 bg-black rounded-lg"
                autoPlay
                muted
                playsInline
              />
              {!cameraActive && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-800 rounded-lg">
                  <div className="text-center text-white">
                    <Camera size={32} className="mx-auto mb-2" />
                    <p className="text-sm">Initializing camera...</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex gap-2 mt-2">
              {!isRecording ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={startRecording}
                  icon={<Camera size={16} />}
                  disabled={!cameraActive || uploadingVideo}
                  className="flex-1"
                >
                  Start Recording
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={stopRecording}
                  icon={<Square size={16} />}
                  className="flex-1 bg-red-600 text-white hover:bg-red-700"
                >
                  Stop Recording
                </Button>
              )}
              
              <div className="flex-1">
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoFileSelect}
                  className="hidden"
                  id="video-upload"
                  disabled={isRecording || uploadingVideo}
                />
                <label htmlFor="video-upload" className="block w-full">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<Upload size={16} />}
                    className="w-full"
                    disabled={isRecording || uploadingVideo}
                  >
                    Upload Video
                  </Button>
                </label>
              </div>
            </div>
          </div>

          {/* Compact Form Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full p-2 text-sm border rounded focus:ring-2 focus:ring-purple-500"
                placeholder="Video title"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full p-2 text-sm border rounded focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full p-2 text-sm border rounded focus:ring-2 focus:ring-purple-500"
              rows={2}
              placeholder="Brief description"
            />
          </div>

          {/* Thumbnail Upload - Compact */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Thumbnail (Optional)
            </label>
            <div className="flex items-center gap-2">
              {formData.thumbnail && (
                <img 
                  src={formData.thumbnail} 
                  alt="Thumbnail" 
                  className="w-12 h-12 object-cover rounded"
                />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleThumbnailFileSelect}
                className="flex-1 text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-purple-50 file:text-purple-700"
                disabled={uploadingThumbnail}
              />
            </div>
          </div>

          {/* Recipients - Compact */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Recipients
            </label>
            <div className="space-y-1">
              {formData.recipients.map((recipient, index) => (
                <div key={index} className="flex gap-1">
                  <select
                    value={recipient}
                    onChange={(e) => handleRecipientChange(index, e.target.value)}
                    className="flex-1 p-1 text-xs border rounded focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select recipient</option>
                    {commonRecipients.map(common => (
                      <option key={common} value={common}>{common}</option>
                    ))}
                  </select>
                  {formData.recipients.length > 1 && (
                    <button
                      onClick={() => handleRemoveRecipient(index)}
                      className="p-1 text-red-600 hover:text-red-700"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddRecipient}
                icon={<Plus size={12} />}
                className="text-xs"
              >
                Add Recipient
              </Button>
            </div>
          </div>

          {/* Upload Progress */}
          {uploadingVideo && (
            <div className="bg-blue-50 p-3 rounded">
              <div className="flex items-center gap-2 mb-1">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                <span className="text-xs font-medium text-blue-900">Uploading...</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-1">
                <div 
                  className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddModal(false)}
              disabled={uploadingVideo}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveVideo}
              disabled={uploadingVideo || !formData.title || !formData.url}
              icon={<Save size={16} />}
            >
              {uploadingVideo ? 'Saving...' : 'Save Video'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* View Video Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title={selectedVideo?.title || 'Video Message'}
        maxWidth="2xl"
      >
        {selectedVideo && (
          <div className="space-y-4">
            <div className="relative">
              <video
                src={selectedVideo.url}
                controls
                className="w-full h-64 bg-black rounded-lg"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
              />
            </div>
            
            <div className="space-y-3">
              {selectedVideo.description && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">Description</h4>
                  <p className="text-sm text-gray-600">{selectedVideo.description}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">Date Recorded</h4>
                  <p className="text-sm text-gray-600">{formatDate(selectedVideo.date)}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">Duration</h4>
                  <p className="text-sm text-gray-600">{selectedVideo.duration}</p>
                </div>
              </div>
              
              {selectedVideo.recipients && selectedVideo.recipients.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">Recipients</h4>
                  <div className="flex flex-wrap gap-1">
                    {selectedVideo.recipients.map((recipient, index) => (
                      <span key={index} className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full text-xs">
                        {recipient}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}